import styled, { css } from 'styled-components';
import is, { isNot } from 'styled-is';
import colors from '@honeybook/hbui/dist/styles/colors';
import spacing from '@honeybook/hbui/dist/styles/spacing';
import Check16 from '@honeybook/icons/dist/icons/Check16';
import { flexCenterAll } from '@honeybook/hbui/dist/styles/mixins';
import { mediumUp } from '@honeybook/hbui/dist/styles/breakpoints';
import HBText from '@honeybook/hbui/dist/components/HBText/HBText';

export const AutomationBlocksContainer = styled.div`
    position: relative;
    padding-top: ${spacing[40]};
    flex-direction: column;
    width: 100%;
    ${flexCenterAll};
    padding-left: 30px;

    ${mediumUp} {
        padding-top: 0;
        padding-bottom: ${spacing[140]};
        padding-left: 0px;
    }
`;

export const AutomationBlockContentContainer = styled.div<{
    isDragging: boolean;
}>`
    height: 100%;
    ${flexCenterAll}
    flex-direction: column;
    gap: 16px;
    ${is('isDragging')`
        display: none;
    `};
`;

export const AutomationBlockActionsContainer = styled.div`
    display: none;
`;

export const AutomationBlockContent = styled.div`
    ${flexCenterAll}
    flex-direction: column;
    width: 100%;
    text-align: center;
`;

export const ActionTypeText = styled(HBText)`
    padding-top: ${spacing[12]};
    padding-bottom: ${spacing[8]};
`;

const greyedOut = css`
    box-shadow: none;
    background-color: ${colors.neutral100};
    padding: 25px;
    border: 1px solid ${colors.neutral400};
`;

export const AutomationBlock = styled.div<{
    isSelected: boolean;
    isDragging: boolean;
    isCompleted: boolean;
    isDeclined: boolean;
    isApplied: boolean;
    isOverdue: boolean;
    placeholderHeight: string;
}>`
    position: relative;
    width: 320px;
    padding: 24px;
    border-radius: 8px;
    border: 2px solid ${colors.white};
    background-color: ${colors.white};
    cursor: pointer;
    transition: box-shadow 150ms, background-color 300ms;
    box-shadow: 0 1px 3px 0 rgba(0, 18, 71, 0.16);

    ${is('isSelected')`
        // !important is to win hover AutomationBlockContainer state
        border: 2px solid ${colors.blue20} !important;
        padding: 24px !important;
        box-shadow: 0 10px 20px 0 rgba(0, 18, 71, 0.15) !important;
    `};

    ${is('isDragging')`
        border-style: dashed;
        // !important is to win hover AutomationBlockContainer and selected state
        border-color: ${colors.neutral800} !important;
        box-shadow: none;
        height: ${props => props.placeholderHeight};
    `};

    ${is('isDeclined')`
        ${greyedOut}
        * {
            color: ${colors.neutral600};
        } 
    `};

    ${is('isApplied')` 
        ${isNot('isCompleted')` 
            ${greyedOut}
        `}
        ${is('isOverdue')`
            &, &:hover {
                border-color: ${colors.orange50} !important;
            }
        `};
    `}
`;

const progressBar = css`
    content: '';
    display: block;
    width: 2px;
    height: 64px;
    background: ${colors.blue20_20};
`;

export const AutomationBlockContainer = styled.div<{
    shouldHideProgressBar: boolean;
}>`
    position: relative;
    width: 500px;
    max-width: 90%;
    ${flexCenterAll};
    flex-direction: column;
    &:after {
        ${progressBar}
    }
    ${is('shouldHideProgressBar')`
        &:last-child:after {
            display: none;
        }
    `}

    &:hover {
        ${AutomationBlockActionsContainer} {
            display: block;
        }

        ${AutomationBlock} {
            border-color: ${colors.blue20_20};
        }
    }
`;

export const AutomationBlockDragContainer = styled.div<{
    ref: any;
    isDragging: boolean;
}>`
    padding: 0 90px;
    position: relative;
    transform: translate3d(0, 0, 0); /* For DnD */
    ${is('isDragging')`
        opacity: 0.2;
    `}
`;

export const BetweenCreateBlockContainer = styled.div`
    ${flexCenterAll};
    z-index: 10;
    min-height: 64px;
    width: 320px;
    margin-top: -64px;
`;

export const AutomationBlockErrorIconContainer = styled.div`
    position: absolute;
    right: 4px;
    top: -14px;
`;

export const StartBlockContainer = styled.div`
    ${flexCenterAll}
    background: ${colors.neutral200};
    column-gap: 8px;
    border-radius: 4px;
    padding: 10px 16px;
    margin: 40px 0 64px;
    position: relative;

    &:after {
        position: absolute;
        top: 100%;
        ${progressBar}
    }
`;

const doneBadgeSize = 26;
export const AutomationDoneTime = styled(HBText)`
    cursor: default;
    position: absolute;
    top: 50%;
    left: -${doneBadgeSize}px;
    text-align: end;
    transform: translate(-100%, -50%);
`;

export const AutomationDoneBadge = styled(Check16)<{
    isEdit: boolean;
}>`
    position: absolute;
    top: 50%;
    left: 0;
    ${flexCenterAll};
    transform: translate(-50%, -50%);
    width: ${doneBadgeSize}px;
    height: ${doneBadgeSize}px;
    border-radius: 100%;
    transform: translate(-50%, -50%);
    background-color: ${colors.green50};
    transition: opacity 150ms;
    ${is('isEdit')`
        opacity: 0.3;
    `}
`;

export const AutomationBlockSkippedIndicator = styled(HBText).attrs({
    type: 'Caption1Light'
})<{
    isSelected: boolean;
}>`
    position: absolute;
    transform: translateY(-50%);
    color: ${colors.white};
    padding: 4px 4px 1px;
    background-color: ${colors.neutral400};
    border-radius: 2px;
    top: 0;
    ${is('isSelected')`
        top: -1px;
    `}
`;
